import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Loader2, Tag } from "lucide-react";
import { blogApi } from "../api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageSEO } from "../hooks/usePageSEO";

const BlogPage = () => {
  usePageSEO();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "Education", "Safety", "Research", "Nutrition"];

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogApi.getAll();
      setPosts(data);
    } catch (err) {
      console.error("[BlogPage] Failed to load blog posts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const filteredPosts = selectedCategory === "All" 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Flatten what was previously a nested ternary in JSX into named branches for readability.
  let blogGridContent;
  if (loading) {
    blogGridContent = (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 text-[#b89a6d] animate-spin" />
        <span className="ml-3 text-[#7a6b62]">Loading articles...</span>
      </div>
    );
  } else if (filteredPosts.length === 0) {
    blogGridContent = (
      <div className="text-center py-20">
        <p className="text-[#7a6b62] text-lg">No articles found in this category.</p>
      </div>
    );
  } else {
    blogGridContent = (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
        {filteredPosts.map((post) => (
          <article
            key={post.id}
            data-testid={`blog-card-${post.slug}`}
            className="bg-white border border-[#f0d8d4] rounded-xl overflow-hidden hover:border-[#d4a5ad] hover:shadow-lg hover:shadow-[#d4a5ad]/20 transition-all duration-300 group"
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-[#f7e8e4] text-[#b89a6d] text-xs font-medium rounded-full">
                  {post.category}
                </span>
                <span className="flex items-center text-[#a09186] text-sm">
                  <Clock className="w-4 h-4 mr-1" />
                  {post.readTime}
                </span>
              </div>

              <h2 className="text-xl font-bold text-[#3a2a26] mb-3 group-hover:text-[#b89a6d] transition-colors">
                {post.title}
              </h2>

              <p className="text-[#7a6b62] mb-4 line-clamp-3">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-[#f0d8d4]">
                <div className="flex items-center text-[#a09186] text-sm">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(post.created_at)}
                </div>

                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`read-more-${post.slug}`}
                  className="flex items-center text-[#b89a6d] hover:text-[#a08560] font-medium text-sm group/link"
                >
                  Read More
                  <ArrowRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">Educational Resources</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#3a2a26] mb-6">
            Peptide Research & Education
          </h1>
          <p className="text-xl text-[#7a6b62] max-w-3xl mx-auto">
            Evidence-based articles on peptide science, wellness and recovery, safety and regulation. 
            Learn about BPC-157 research, TB-500 studies, and how to evaluate peptide claims.
          </p>
        </div>
      </section>

      <section className="py-8 bg-[#fffaf5] border-b border-[#f0d8d4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`blog-category-${category.toLowerCase()}`}
                className={`px-5 py-2 rounded-lg font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-[#d4a5ad] text-white"
                    : "bg-white text-[#7a6b62] hover:bg-[#f7e8e4] border border-[#f0d8d4]"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fffaf5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {blogGridContent}
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <h3 className="text-[#b89a6d] font-bold text-lg mb-3">Educational Content Disclaimer</h3>
            <p className="text-[#7a6b62] text-sm leading-relaxed">
              All articles on this website are for educational and informational purposes only. 
              They do not constitute medical advice, diagnosis, or treatment recommendations. 
              The information provided should not be used as a substitute for professional medical guidance. 
              Always consult with qualified healthcare providers before making any health-related decisions.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
